import { Check, X, Minus, TrendingUp, BarChart3, Zap, Shield } from "lucide-react";

const statusColors = {
  good: { bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-400" },
  bad: { bg: "bg-red-500/15", border: "border-red-500/30", text: "text-red-400", dot: "bg-red-400" },
  neutral: { bg: "bg-amber-500/10", border: "border-amber-500/25", text: "text-amber-400", dot: "bg-amber-400" },
};

function ProScoreRing({ score, size = 90 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 70 ? "#22c55e" : score >= 40 ? "#eab308" : "#ef4444";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#1e293b" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="6"
          strokeLinecap="round" strokeDasharray={`${progress} ${circumference}`}
          style={{ filter: `drop-shadow(0 0 8px ${color}40)`, transition: "stroke-dasharray 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-2xl font-black" style={{ color }}>{score}</span>
        <span className="text-[8px] text-slate-500 uppercase tracking-widest">Score</span>
      </div>
    </div>
  );
}

function RatioCard({ ratio }) {
  const sc = statusColors[ratio.status] || statusColors.neutral;
  const fmtValue = (v, unit) => {
    if (v === null || v === undefined) return "N/A";
    if (unit === "$") {
      const abs = Math.abs(v);
      if (abs >= 1e12) return `${(v/1e12).toFixed(1)}T$`;
      if (abs >= 1e9) return `${(v/1e9).toFixed(1)}B$`;
      if (abs >= 1e6) return `${(v/1e6).toFixed(0)}M$`;
      return `${v.toLocaleString("fr-FR")}$`;
    }
    return `${v}${unit}`;
  };

  return (
    <div className={`rounded-lg p-3 ${sc.bg} border ${sc.border} transition-all hover:scale-[1.02]`}
      data-testid={`ratio-${ratio.key}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{ratio.label}</span>
        <span className={`w-2 h-2 rounded-full ${sc.dot}`} />
      </div>
      <div className={`font-mono text-lg font-black ${sc.text}`}>
        {fmtValue(ratio.value, ratio.unit)}
      </div>
      <div className={`text-[10px] ${sc.text} font-medium mt-0.5`}>{ratio.verdict}</div>
    </div>
  );
}

function SignalRow({ signal }) {
  const sc = statusColors[signal.status] || statusColors.neutral;
  return (
    <div className={`flex items-center gap-3 py-2.5 px-3 rounded-lg ${signal.active ? sc.bg : "bg-slate-800/30"} border ${signal.active ? sc.border : "border-slate-700/30"} transition-all`}
      data-testid={`signal-${signal.key}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${signal.active ? sc.bg + " " + sc.border : "bg-slate-700/50 border-slate-600/50"} border`}>
        {signal.active ? <Check className={`w-3.5 h-3.5 ${sc.text}`} /> : <Minus className="w-3 h-3 text-slate-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-bold ${signal.active ? sc.text : "text-slate-400"}`}>{signal.label}</div>
        <div className="text-[10px] text-slate-500 truncate">{signal.detail}</div>
      </div>
    </div>
  );
}

export default function FundamentalsProPanel({ data }) {
  if (!data || !data.classic_ratios?.length) return null;

  const { score, classic_ratios, hedge_fund_ratios, growth_signals, checklist, active_signals, total_signals } = data;

  return (
    <div className="glass-card rounded-xl p-4 sm:p-5 mb-4 space-y-5" data-testid="fundamentals-pro-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-cyan-400" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Analyse Fondamentale Pro</h3>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5 ml-7">11 ratios professionnels + 7 signaux de croissance</p>
        </div>
        <ProScoreRing score={score || 0} />
      </div>

      {/* Pro Checklist Summary */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/40 border border-slate-700/40">
        <Shield className="w-4 h-4 text-cyan-400 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Checklist Pro Investisseur</div>
          <div className="flex items-center gap-3 flex-wrap">
            {[
              ["PER < 30", checklist?.per_ok],
              ["PEG < 1.5", checklist?.peg_ok],
              ["ROIC > 15%", checklist?.roic_ok],
              ["Croiss. > 15%", checklist?.earnings_growth_ok],
              ["Dette < 3x", checklist?.debt_ok],
            ].map(([label, ok], i) => (
              <div key={i} className="flex items-center gap-1">
                {ok ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-red-400" />}
                <span className={`text-[10px] font-mono ${ok ? "text-emerald-400" : "text-red-400"}`}>{label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`font-mono text-2xl font-black ${(checklist?.total || 0) >= 4 ? "text-emerald-400" : (checklist?.total || 0) >= 2 ? "text-amber-400" : "text-red-400"}`}>
            {checklist?.total || 0}<span className="text-sm text-slate-500">/5</span>
          </div>
        </div>
      </div>

      {/* Classic Ratios */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-blue-400" />
          <span className="text-xs text-blue-400 font-bold uppercase tracking-wider">Ratios Classiques</span>
          <span className="text-[9px] text-slate-600 ml-1">Valorisation &amp; Qualité</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {classic_ratios.map(r => <RatioCard key={r.key} ratio={r} />)}
        </div>
      </div>

      {/* Hedge Fund Ratios */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="text-xs text-amber-400 font-bold uppercase tracking-wider">Ratios Hedge Fund</span>
          <span className="text-[9px] text-slate-600 ml-1">Croissance &amp; Rentabilité</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {hedge_fund_ratios.map(r => <RatioCard key={r.key} ratio={r} />)}
        </div>
      </div>

      {/* Growth Signals */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-400 font-bold uppercase tracking-wider">Signaux de Croissance</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`font-mono text-sm font-black ${(active_signals || 0) >= 5 ? "text-emerald-400" : (active_signals || 0) >= 3 ? "text-amber-400" : "text-red-400"}`}>
              {active_signals || 0}
            </span>
            <span className="text-[10px] text-slate-500">/ {total_signals || 7} actifs</span>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(growth_signals || []).map(s => <SignalRow key={s.key} signal={s} />)}
        </div>
      </div>
    </div>
  );
}
